'use client';

import React, { useState, useEffect } from 'react';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedLocationId?: number;
}

interface Location {
  id: number;
  name: string;
  city: string;
  type: string;
}

const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose, preselectedLocationId }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [formData, setFormData] = useState({
    date: '',
    guests: '',
    service: '',
    location_id: preselectedLocationId?.toString() || '',
    special_requests: '',
    name: '',
    phone: '',
    email: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch('/api/locations');
        if (response.ok) {
          const data = await response.json();
          setLocations(data.filter((l: Location) => l.type === 'buffet'));
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    if (preselectedLocationId) {
      setFormData(prev => ({ ...prev, location_id: preselectedLocationId.toString() }));
    }
  }, [preselectedLocationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('/api/reservations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setTimeout(() => {
          onClose();
          setStatus('idle');
          setFormData({
            date: '', guests: '', service: '', location_id: preselectedLocationId?.toString() || '',
            special_requests: '', name: '', phone: '', email: ''
          });
        }, 2000);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 modal-overlay ${isOpen ? 'open' : ''}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-charcoal/80 backdrop-blur-sm" />

      {/* Panel */}
      <div className="modal-panel relative w-full max-w-lg bg-parchment rounded-4xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-parchment/95 backdrop-blur-sm px-8 pt-8 pb-4 border-b border-clay/15 flex items-center justify-between">
          <div>
            <p className="text-clay text-xs uppercase tracking-widest font-sans font-medium mb-1">
              CorsAsia
            </p>
            <h2 className="font-serif italic text-charcoal text-2xl">Réserver une table</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer la modale de réservation"
            className="w-10 h-10 rounded-full border border-clay/20 flex items-center justify-center text-charcoal/50 hover:text-charcoal hover:border-clay transition-all"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        {status === 'success' ? (
          <div className="px-8 py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-serif text-charcoal text-xl mb-2">Réservation envoyée !</p>
            <p className="font-sans text-charcoal/50 text-sm">Nous vous confirmons par email sous 24h.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
            {/* Restaurant */}
            <div>
              <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                Restaurant
              </label>
              <select
                value={formData.location_id}
                onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                className="form-input w-full rounded-xl px-4 py-3 text-sm"
                required
              >
                <option value="">Sélectionner un restaurant</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name} {loc.city}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                Date de réservation
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="form-input w-full rounded-xl px-4 py-3 text-sm"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                  Nombre de convives
                </label>
                <select
                  value={formData.guests}
                  onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                  className="form-input w-full rounded-xl px-4 py-3 text-sm"
                  required
                >
                  <option value="">Sélectionner</option>
                  <option value="2">2 personnes</option>
                  <option value="3">3 personnes</option>
                  <option value="4">4 personnes</option>
                  <option value="5">5 personnes</option>
                  <option value="6">6 personnes</option>
                  <option value="7">7 personnes</option>
                  <option value="8">8 personnes</option>
                  <option value="10">10+ personnes</option>
                </select>
              </div>

              <div>
                <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                  Service
                </label>
                <select
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="form-input w-full rounded-xl px-4 py-3 text-sm"
                  required
                >
                  <option value="">Sélectionner</option>
                  <option value="lunch">Déjeuner</option>
                  <option value="dinner">Dîner</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                Remarques particulières
              </label>
              <textarea
                rows={3}
                value={formData.special_requests}
                onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                placeholder="Allergies, préférences, occasion spéciale..."
                className="form-input w-full rounded-xl px-4 py-3 text-sm resize-none leading-relaxed"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                  Nom
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Votre nom"
                  className="form-input w-full rounded-xl px-4 py-3 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="06 00 00 00 00"
                  className="form-input w-full rounded-xl px-4 py-3 text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block font-sans text-xs uppercase tracking-widest text-charcoal/50 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="vous@exemple.com"
                className="form-input w-full rounded-xl px-4 py-3 text-sm"
                required
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-corsasia text-parchment py-4 rounded-full font-sans font-semibold text-sm uppercase tracking-widest hover:bg-corsasia-dark transition-all duration-300 shadow-lg disabled:opacity-50"
              >
                {status === 'loading' ? 'Envoi en cours...' : 'Confirmer la réservation'}
              </button>
              {status === 'error' && (
                <p className="mt-3 text-center text-red-500 text-xs">
                  Une erreur est survenue. Veuillez réessayer.
                </p>
              )}
              <p className="mt-3 text-center font-sans text-charcoal/30 text-xs">
                Nous vous confirmons votre réservation par email sous 24h.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingModal;